import React, { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import MainButton from 'views/components/button';
import { ConfigurationFormWrapper } from '../AppointmetTypesForm.style';
import {
  IDTAppointmentSubType,
  DTAppointmentSubType
} from 'model/DTAppointmentSubType';
import {
  Input,
  Select,
  Timepicker
} from 'api/sharedComponents/reactHookFormComponents';
import { Lookup } from 'model/lookup';
import { yupResolver } from '@hookform/resolvers/yup';
import { DT_APPOINTMENT_SUBTYPE_SCHEMA } from 'utils/validators/configurations/DTAppointmentSubType';
import {
  IAppointmentType,
  SalesForceTherapyMappingsType
} from 'model/appointment';
import { mapAppointmentTitlesToOptions } from 'utils/mappers/form';
import { useMutation } from '@apollo/react-hooks';
import { GET_DT_APPOINTMENT_SUBTYPES } from 'api/graphql/v2/queries/AppointmentTypes';
import { PUT_DT_APPOINTMENT_SUBTYPE } from 'api/graphql/v2/mutations/AppointmentTypes';
import FormItem from 'api/sharedComponents/reactHookFormComponents/formItem';
import { formatDTSubTypeToInput } from 'utils/format/appointmentType';
import { EVENT_TYPES } from 'utils/constants/appointmentsTypes';
import { mapDataToOptions } from '../AppointmentTypesForm';

interface Props {
  superTypeList?: IAppointmentType[];
  salesForceData?: SalesForceTherapyMappingsType[];
  initialValues: IDTAppointmentSubType;
  onFormClose: () => void;
  title: string;
  currentStatusFilter?: boolean;
  // Optional overrides so this form can be reused for Counseling subtypes, which
  // persist to a dedicated table via their own mutation. Defaults are DT.
  mutationDoc?: any;
  refetchQueryDoc?: any;
  eventType?: string;
  formatToInput?: (
    data: IDTAppointmentSubType,
    initialValues: IDTAppointmentSubType
  ) => any;
}

const DTAppointmentSubTypesForm: React.FC<Props> = ({
  superTypeList,
  salesForceData,
  initialValues,
  title,
  onFormClose,
  currentStatusFilter,
  mutationDoc,
  refetchQueryDoc,
  eventType,
  formatToInput
}: Props) => {
  const {
    handleSubmit,
    reset,
    control,
    errors,
    formState: { isSubmitting, isDirty }
  } = useForm<IDTAppointmentSubType>({
    defaultValues: initialValues,
    resolver: yupResolver(DT_APPOINTMENT_SUBTYPE_SCHEMA())
  });

  const superTypeListOptions = useMemo(
    () => (superTypeList ? mapAppointmentTitlesToOptions(superTypeList) : []),
    [superTypeList]
  );

  const salesForceTherapyListOptions = useMemo(
    () => (salesForceData ? mapDataToOptions(salesForceData) : []),
    [salesForceData]
  );

  const [submitDTAppointmentSubType] = useMutation(
    mutationDoc ?? PUT_DT_APPOINTMENT_SUBTYPE
  );

  const onSubmit = handleSubmit(async (data: IDTAppointmentSubType) => {
    await submitDTAppointmentSubType({
      variables: {
        settingsWithAppointment: (formatToInput ?? formatDTSubTypeToInput)(
          data,
          initialValues
        ),
        eventType: eventType ?? EVENT_TYPES.DT
      },
      refetchQueries: [
        {
          query: refetchQueryDoc ?? GET_DT_APPOINTMENT_SUBTYPES,
          variables: {
            isActive: currentStatusFilter
          }
        }
      ]
    }).then(onFormClose);
  });

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  return (
    <ConfigurationFormWrapper>
      <div className="header">
        <h3>{title}</h3>
        <button className="ui icon button close-button" onClick={onFormClose}>
          <i className="close icon"></i>
        </button>
      </div>

      <form onSubmit={onSubmit}>
        <FormItem label="Title">
          <Controller
            name={DTAppointmentSubType.title}
            control={control}
            render={fieldProps => (
              <Input
                field={fieldProps}
                placeholder="Input title"
                errors={errors}
              />
            )}
          />
        </FormItem>

        <FormItem label="Status">
          <Controller
            name={DTAppointmentSubType.isActive}
            control={control}
            render={fieldProps => (
              <Select
                placeholder="Select Status"
                options={[
                  { label: 'Active', value: true },
                  { label: 'Inactive', value: false }
                ]}
                field={fieldProps}
                errors={errors}
              />
            )}
          />
        </FormItem>

        {initialValues?.id !== 0 && (
          <FormItem label="ID">
            <Controller
              name={DTAppointmentSubType.id}
              control={control}
              render={fieldProps => (
                <Input
                  field={fieldProps}
                  placeholder="Input ID"
                  errors={errors}
                  disabled
                />
              )}
            />
          </FormItem>
        )}
        <FormItem label="Athena Appointment Type ID">
          <Controller
            name={DTAppointmentSubType.athenaId}
            control={control}
            render={fieldProps => (
              <Input
                placeholder="Input Athena Appointment Type ID"
                field={fieldProps}
                errors={errors}
              />
            )}
          />
        </FormItem>
        <FormItem label="Parent Type">
          <Controller
            name={`${DTAppointmentSubType.superType}.${Lookup.id}`}
            control={control}
            render={fieldProps => (
              <Select
                options={superTypeListOptions}
                placeholder="Select Parent Type"
                field={fieldProps}
                errors={errors}
              />
            )}
          />
        </FormItem>
        <FormItem label="Duration">
          <Controller
            name={DTAppointmentSubType.duration}
            control={control}
            render={fieldProps => (
              <Timepicker
                name={DTAppointmentSubType.duration}
                minuteStep={15}
                format="HH:mm"
                disabled={false}
                field={fieldProps}
                errors={errors}
              />
            )}
          />
        </FormItem>
        <FormItem label="Salesforce Therapy Title">
          <Controller
            name={`${DTAppointmentSubType.salesForceMapping}.id`}
            control={control}
            defaultValue={null}
            render={fieldProps => (
              <Select
                placeholder="Select Salesforce Title"
                options={salesForceTherapyListOptions}
                field={fieldProps}
                errors={errors}
              />
            )}
          />
        </FormItem>
        <MainButton
          className="submit-button"
          title="Save"
          onClick={() => {
            onSubmit();
          }}
          disabled={isSubmitting || !isDirty}
          loading={isSubmitting}
        />
      </form>
    </ConfigurationFormWrapper>
  );
};
export default React.memo(DTAppointmentSubTypesForm);
